import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';

import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useAuthUI } from '../context/AuthUIContext';
import CartLineItem from '../components/cart/CartLineItem';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { formatPrice } from '../utils/money';

export default function CartPage() {
  const { items, subtotal, count, balanceDue, amountDueNow, source, loading, clearCart } = useCart();
  const { user } = useAuth();
  const { openLogin } = useAuthUI();
  const navigate = useNavigate();

  const goCheckout = () => {
    if (user) navigate('/checkout');
    else openLogin();
  };

  if (loading) {
    return (
      <div className="ss-container ss-state">
        <Spinner label="Loading cart" size={32} />
      </div>
    );
  }

  return (
    <section className="ss-container" style={{ paddingBlock: 'var(--ss-space-6)' }}>
      <h1>Your cart</h1>

      {items.length === 0 ? (
        <div className="ss-cart-empty">
          <ShoppingCart size={48} aria-hidden="true" />
          <p style={{ marginTop: 'var(--ss-space-3)' }}>Your cart is empty.</p>
          <p style={{ marginTop: 'var(--ss-space-4)' }}>
            <Link className="ss-btn ss-btn--primary" to="/products">Start shopping</Link>
          </p>
        </div>
      ) : (
        <div className="ss-cart-page" style={{ marginTop: 'var(--ss-space-5)' }}>
          <div>
            {items.map((i) => (
              <CartLineItem key={i.productId} item={i} />
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--ss-space-4)' }}>
              <Link to="/products">← Continue shopping</Link>
              <button type="button" className="ss-linkbtn" onClick={clearCart}>Clear cart</button>
            </div>
          </div>

          <Card className="ss-cart-summary">
            <h2 style={{ fontSize: '1.25rem', marginBottom: 'var(--ss-space-3)' }}>Summary</h2>
            {balanceDue > 0 ? (
              <>
                <div className="ss-summary-line ss-summary-line--muted">
                  <span>Subtotal ({count})</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="ss-cart-subtotal">
                  <span>Due now</span>
                  <span>{formatPrice(amountDueNow)}</span>
                </div>
                <div className="ss-summary-line ss-summary-line--muted">
                  <span>Balance due later</span>
                  <span>{formatPrice(balanceDue)}</span>
                </div>
              </>
            ) : (
              <div className="ss-cart-subtotal">
                <span>Subtotal ({count})</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
            )}
            <p style={{ color: 'var(--ss-brown-hover)', fontSize: '0.85rem', marginBlock: 'var(--ss-space-3)' }}>
              Shipping &amp; taxes calculated at checkout.
            </p>
            <Button onClick={goCheckout} className="ss-btn" style={{ width: '100%' }}>
              Checkout
            </Button>
            {source === 'guest' && (
              <p className="ss-cart-note" style={{ marginTop: 'var(--ss-space-3)' }}>
                Log in to save your cart across devices.
              </p>
            )}
          </Card>
        </div>
      )}
    </section>
  );
}
